// activityModel.ts — pure helpers for the Activity page, its wall mode, the
// live dock, and the per-PR events panel. Deterministic given `now`.

import type { Tone } from '../../components/tone/tone';
import { endpoints } from '../../api/endpoints';
import type { PipelineEvent, PipelineEventsQuery, PipelineEventsResponse } from '../../api/types';
import { clockText, isSameLocalDay, localDayKey, zoneLabel } from '../../format/when';
import { repoRefOf, repoUrl } from '../repoBrowserModel';
import { todoHref } from '../shift/workPaths';

export const ACTIVITY_PATH = '/activity';
export const ACTIVITY_PAGE_SIZE = 100;
export const DAY_MS = 24 * 60 * 60 * 1000;

/** URL-held filters. `kind` is a prefix when it ends with `.` (server rule). */
export interface ActivityFilters {
  family: string;
  repo: string;
  source: string;
  kind: string;
  todo_id: string;
  pr: string;
  needs_human: boolean;
}

export const FILTER_KEYS = ['family', 'repo', 'source', 'kind', 'todo_id', 'pr'] as const;

export function parseActivityFilters(params: URLSearchParams): ActivityFilters {
  const text = (key: string): string => (params.get(key) ?? '').trim();
  return {
    family: text('family'),
    repo: text('repo'),
    source: text('source'),
    // A kind the server would refuse is dropped rather than sent: it answers
    // `422 events_invalid_query` and the whole feed would look broken.
    kind: isKindFilter(text('kind')) ? text('kind') : '',
    todo_id: text('todo_id'),
    pr: PR_PATTERN.test(text('pr')) ? text('pr') : '',
    needs_human: params.get('needs_human') === '1' || params.get('needs_human') === 'true',
  };
}

/** Longest kind the server stores, in characters. */
export const MAX_KIND_CHARS = 64;

const PR_PATTERN = /^\d+$/;

/** One word of a kind: `^[a-z][a-z0-9_]*$`. */
function isKindWord(word: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(word);
}

/** The server's rule: two or more dotted lower-case words, or such a word
 *  followed by a dot, which reads as a prefix. */
export function isKind(kind: string): boolean {
  const words = kind.split('.');
  return kind.length <= MAX_KIND_CHARS && words.length >= 2 && words.every(isKindWord);
}

/** A `kind` filter the server would take: one kind or prefix, or — for the
 *  chips that span several — a comma-separated list of them, each of which is
 *  narrowed here instead (see `matchesKind`). */
export function isKindFilter(kind: string): boolean {
  const patterns = kindPatterns(kind);
  return (
    patterns.length > 0 &&
    patterns.every((pattern) => (pattern.endsWith('.') ? isKind(`${pattern}x`) : isKind(pattern)))
  );
}

/** Why a typed filter value cannot be used, for the field that holds it, or
 *  null when it can. An empty field is no filter at all, so never an error. */
export function filterFieldError(key: string, value: string): string | null {
  const text = value.trim();
  if (text === '') return null;
  if (key === 'kind' && !isKindFilter(text)) {
    return 'A kind is dotted lower-case words (gate.finished), or a prefix ending in a dot (todo.).';
  }
  if (key === 'pr' && !PR_PATTERN.test(text)) return 'A pull request is its number: digits only.';
  return null;
}

/**
 * The repository a typed Repo filter means. The server matches `owner/name`
 * exactly, so a bare name finds nothing; when the repositories the reader can
 * see name exactly one `.../<name>`, that is what they meant, and the field
 * commits the full name. Ambiguous or unknown, the text is left as typed: the
 * feed comes back empty rather than silently pinned to the wrong repository.
 */
export function resolveRepoFilter(value: string, repos: readonly string[]): string {
  const text = value.trim();
  if (text === '' || text.includes('/')) return text;
  const wanted = text.toLowerCase();
  const full = [
    ...new Set(repos.filter((name) => name.slice(name.indexOf('/') + 1).toLowerCase() === wanted)),
  ];
  return full.length === 1 ? full[0] : text;
}

export function isWallMode(params: URLSearchParams): boolean {
  return params.get('wall') === '1';
}

export function filtersToQuery(filters: ActivityFilters): PipelineEventsQuery {
  const query: PipelineEventsQuery = {};
  if (filters.family) query.family = filters.family;
  if (filters.repo) query.repo = filters.repo;
  if (filters.source) query.source = filters.source;
  // The server takes one kind (or one prefix). A chip that spans several
  // (`review.,pr.review`) asks for everything and narrows here: `matchesKind`.
  if (filters.kind && !filters.kind.includes(',')) query.kind = filters.kind;
  if (filters.todo_id) query.todo_id = filters.todo_id;
  if (filters.pr) query.pr = Number(filters.pr);
  if (filters.needs_human) query.needs_human = true;
  return query;
}

export function hasActiveFilters(filters: ActivityFilters): boolean {
  return filters.needs_human || FILTER_KEYS.some((key) => filters[key] !== '');
}

/** Link to the Activity page with some filters set. */
export function activityHref(filters: Partial<Record<(typeof FILTER_KEYS)[number], string | number>>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') qs.set(key, String(value));
  }
  const suffix = qs.toString();
  return suffix ? `${ACTIVITY_PATH}?${suffix}` : ACTIVITY_PATH;
}

/**
 * The exact request this page's list is one page of, so the screen and the API
 * are visibly the same thing: the filters as the server sees them, with the
 * page size the feed asks for. The cursors (`after_seq`, `before_seq`) are the
 * live tail's and the older loader's, not the view's, so they are left out.
 */
export function eventsApiPath(query: PipelineEventsQuery): string {
  return endpoints.events({ ...query, limit: ACTIVITY_PAGE_SIZE });
}

/**
 * One event as the API returned it, pretty-printed: what an agent or an
 * operator pastes into a ticket or a script. The keys are in the order the
 * server sent them, so a row's JSON reads like the response it came from.
 */
export function eventJson(event: PipelineEvent): string {
  return `${JSON.stringify(event, null, 2)}\n`;
}

/** The kinds a `kind` filter names: one, or several separated by commas. */
export function kindPatterns(kind: string): string[] {
  return kind
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

/** The server's rule, applied here too: exact, or a prefix when it ends with `.`. */
export function matchesKind(eventKind: string, kind: string): boolean {
  const patterns = kindPatterns(kind);
  if (patterns.length === 0) return true;
  return patterns.some((pattern) =>
    pattern.endsWith('.') ? eventKind.startsWith(pattern) : eventKind === pattern
  );
}

/** Minutes two reports of one happening may lie apart and still be one happening. */
export const SAME_HAPPENING_MS = 5 * 60 * 1000;

const PLAIN_GATE_OUTCOMES = new Set(['success', 'failure']);

/** The forge's heartbeat schema knows success, failure and error; the runner's
 *  own report also says timed_out and inputs_changed, and the heartbeat
 *  flattens both of those to error. */
function sameGateOutcome(log: string | null, finished: string | null): boolean {
  if (log === finished) return true;
  return finished === 'error' && log !== null && !PLAIN_GATE_OUTCOMES.has(log);
}

/**
 * One row per happening. A finished gate is reported twice: `gate.finished`
 * (the forge, from the runner's heartbeat) and `gate.log` (the runner's
 * reporter, seconds later, with the log tail). The same goes for a review:
 * `review.finished` (heartbeat) and `pr.review` (the verdict itself). Keep the
 * one that carries more (`gate.log`, `pr.review`) and drop its echo. A lone
 * report of either kind is kept.
 */
export function foldEchoes(events: PipelineEvent[]): PipelineEvent[] {
  const logs = events.filter((event) => event.kind === 'gate.log');
  const verdicts = events.filter((event) => event.kind === 'pr.review');
  if (logs.length === 0 && verdicts.length === 0) return events;
  return events.filter((event) => {
    if (event.kind === 'gate.finished') {
      const at = Date.parse(event.ts);
      return !logs.some(
        (log) =>
          log.repo !== null &&
          log.repo === event.repo &&
          log.sha !== null &&
          log.sha === event.sha &&
          sameGateOutcome(log.outcome, event.outcome) &&
          Math.abs(Date.parse(log.ts) - at) <= SAME_HAPPENING_MS
      );
    }
    if (event.kind === 'review.finished') {
      return !verdicts.some(
        (verdict) =>
          verdict.repo !== null &&
          verdict.repo === event.repo &&
          verdict.pr !== null &&
          verdict.pr === event.pr &&
          verdict.sha !== null &&
          verdict.sha === event.sha
      );
    }
    return true;
  });
}

/** Whether a `kind` filter asks for the slots' own step beats, in which case
 *  they are what the reader came for and are never folded away. */
export function wantsWorkerSteps(kind: string): boolean {
  return kindPatterns(kind).some((pattern) => matchesKind(WORKER_STAGE_KIND, pattern));
}

/**
 * What a list shows: echoes folded, a multi-kind chip applied, and — unless
 * asked for — the slots' step beats left out. `worker.stage` is more than a
 * quarter of the feed and says nothing about an outcome, so on All it buried
 * the gates and the merges under a slot narrating itself. The beats are one
 * toggle away, and the Workers chip asks for them by name.
 */
export function visibleEvents(
  events: PipelineEvent[],
  kind = '',
  workerSteps = false
): PipelineEvent[] {
  const folded = foldEchoes(events);
  const narrowed = kind.includes(',') ? folded.filter((event) => matchesKind(event.kind, kind)) : folded;
  if (workerSteps || wantsWorkerSteps(kind)) return narrowed;
  return narrowed.filter((event) => event.kind !== WORKER_STAGE_KIND);
}

/** How many step beats a page is holding back: what the toggle offers. */
export function workerStepCount(events: PipelineEvent[]): number {
  return events.filter((event) => event.kind === WORKER_STAGE_KIND).length;
}

/** `?steps=1`: the reader asked to see the slots' step beats on every view. */
export function showsWorkerSteps(params: URLSearchParams): boolean {
  return params.get('steps') === '1';
}

/** The URL params the worker-steps toggle sets, keeping every other filter. */
export function applyWorkerSteps(params: URLSearchParams, show: boolean): URLSearchParams {
  const next = new URLSearchParams(params);
  if (show) next.set('steps', '1');
  else next.delete('steps');
  return next;
}

/** Union by `seq`, newest first. Later arguments win on a duplicate seq. */
export function mergeEvents(...lists: Array<PipelineEvent[] | undefined>): PipelineEvent[] {
  const bySeq = new Map<number, PipelineEvent>();
  for (const list of lists) {
    for (const event of list ?? []) bySeq.set(event.seq, event);
  }
  return [...bySeq.values()].sort((a, b) => b.seq - a.seq);
}

export function maxSeq(events: PipelineEvent[]): number {
  return events.reduce((max, event) => Math.max(max, event.seq), 0);
}

export function minSeq(events: PipelineEvent[]): number | null {
  return events.length === 0 ? null : events.reduce((min, event) => Math.min(min, event.seq), Infinity);
}

/**
 * Whether the log holds events behind a newest-first page. The server says so
 * with `has_more`, which is the only honest answer: a page is folded and
 * filtered before it is drawn, so neither the rows on screen nor the events in
 * the response say whether the next cursor would return anything. A server
 * that does not send `has_more` falls back to the page being full.
 */
export function hasOlderEvents(
  page: Pick<PipelineEventsResponse, 'events' | 'has_more'> | undefined,
  pageSize: number
): boolean {
  if (!page) return false;
  return typeof page.has_more === 'boolean' ? page.has_more : page.events.length >= pageSize;
}

export type EventTone = Tone;

const BAD = new Set(['failure', 'failed', 'error', 'blocked', 'timed_out', 'request_changes', 'inputs_changed']);
const SHAKY = new Set(['retry', 'ratelimit', 'dequeued', 'pending', 'in_progress', 'queued', 'hold']);
const GOOD = new Set(['success', 'done', 'approve', 'approved', 'landed', 'merged']);

/** Red only when a human is needed; a failure the pipeline owns gets its own
 *  tone, and a clean finish is green. */
export function eventTone(event: Pick<PipelineEvent, 'needs_human' | 'outcome'>): EventTone {
  if (event.needs_human) return 'human';
  const outcome = (event.outcome ?? '').toLowerCase();
  if (BAD.has(outcome)) return 'failed';
  if (SHAKY.has(outcome)) return 'warn';
  if (GOOD.has(outcome)) return 'ok';
  return 'unknown';
}

export interface EventLink {
  label: string;
  to: string;
}

/** SPA path of a forge repo given as `owner/name`, on the forge it is on. */
export function repoHref(host: string, repo: string): string {
  return repoUrl(repoRefOf(host, repo));
}

export function pullHref(host: string, repo: string, pr: number): string {
  return repoUrl(repoRefOf(host, repo), 'pulls', String(pr));
}

/**
 * Where an event's join keys lead: its todo, its PR, its repo. There is no
 * commit page in the app, so a sha is shown as text and the PR link carries it.
 */
export function eventLinks(event: PipelineEvent, host: string): EventLink[] {
  const links: EventLink[] = [];
  if (event.todo_id && event.family) {
    links.push({
      label: `todo ${event.todo_id}`,
      to: todoHref(event.todo_id, event.family),
    });
  }
  if (event.repo && event.pr) {
    links.push({ label: `${event.repo}#${event.pr}`, to: pullHref(host, event.repo, event.pr) });
  } else if (event.repo) {
    links.push({ label: event.repo, to: repoHref(host, event.repo) });
  }
  return links;
}

/**
 * The Flow station an event belongs to (docs/flow.md, Stations), or null for a
 * kind the route has no station for. Matching is by kind prefix so a new
 * `gate.*` or `queue.*` kind lands on its station without a list to update.
 */
export function eventStationId(kind: string): string | null {
  if (kind === 'todo.filed') return 'queue';
  if (kind.startsWith('todo.') || kind.startsWith('worker.')) return 'work';
  if (kind.startsWith('shift.')) return 'shift';
  if (kind.startsWith('review.') || kind === 'pr.opened' || kind === 'pr.review' || kind === 'pr.approved') {
    return 'review';
  }
  if (kind.startsWith('gate.')) return 'gate';
  if (kind.startsWith('queue.') || kind === 'pr.merged') return 'merge';
  if (kind.startsWith('pin.')) return 'pin';
  if (kind.startsWith('release.')) return 'stage';
  if (kind.startsWith('deploy.')) return 'deploy';
  return null;
}

/** Station ids in the words a link says. */
const STATION_NAMES: Record<string, string> = {
  queue: 'Queue',
  work: 'Work',
  shift: 'Shift',
  review: 'Review',
  gate: 'Gate',
  merge: 'Merge',
  pin: 'Pin',
  stage: 'Stage',
  deploy: 'Deploy',
};

/** The link from an event to its station on Flow, or null when it has none. */
export function eventStationLink(kind: string): EventLink | null {
  const id = eventStationId(kind);
  if (!id) return null;
  return { label: `${STATION_NAMES[id] ?? id} on Flow`, to: `/flow?station=${encodeURIComponent(id)}` };
}

export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
}

/**
 * The longest duration each kind reached in the loaded list. A row's bar is
 * scaled against its own kind: a gate that runs for minutes and an attempt
 * that runs for an hour say nothing about each other, so they are never put on
 * one scale.
 */
export function longestSecondsByKind(events: Array<Pick<PipelineEvent, 'kind' | 'seconds'>>): Map<string, number> {
  const longest = new Map<string, number>();
  for (const event of events) {
    const seconds = event.seconds;
    if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) continue;
    longest.set(event.kind, Math.max(longest.get(event.kind) ?? 0, seconds));
  }
  return longest;
}

/** The shortest bar that still reads as a bar, as a share of the full width. */
export const MIN_DURATION_SHARE = 0.04;

/**
 * How much of its row the duration bar fills, 0..1, or null when there is
 * nothing to draw: no duration, or no longest of this kind to scale against.
 * The longest row of its kind fills the width; a very quick one keeps a sliver
 * so the bar is still visible.
 */
export function durationShare(
  seconds: number | null | undefined,
  longestOfKind: number | null | undefined
): number | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (typeof longestOfKind !== 'number' || !Number.isFinite(longestOfKind) || longestOfKind <= 0) return null;
  const share = Math.min(seconds, longestOfKind) / longestOfKind;
  return Math.max(share, MIN_DURATION_SHARE);
}

/** The duration as it was reported, for the title beside the rounded text. */
export function exactSecondsText(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '';
  const exact = Number(seconds.toFixed(3));
  return `${exact} second${exact === 1 ? '' : 's'}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A day as an operator reads it: `Today · 19 Sep 2026 · CEST`, `Yesterday · …`,
 * else the date alone. Clock times carry no date and no zone, so both are said
 * once, above the rows they cover.
 */
export function formatDay(iso: string, now: Date): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const date = `${at.getDate()} ${MONTHS[at.getMonth()]} ${at.getFullYear()}`;
  const days = Math.round(
    (Date.parse(`${localDayKey(now)}T00:00:00Z`) - Date.parse(`${localDayKey(iso)}T00:00:00Z`)) / DAY_MS
  );
  const named = days === 0 ? `Today · ${date}` : days === 1 ? `Yesterday · ${date}` : date;
  return `${named} · ${zoneLabel(at)}`;
}

/**
 * How far back the loaded list reaches: the clock alone for a list that starts
 * today, the date with it when it reaches back past midnight. Minutes are
 * enough — this is a boundary, not an event's own stamp.
 */
export function loadedSinceText(iso: string, now: Date): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const clock = clockText(at).slice(0, 5);
  return isSameLocalDay(at, now) ? clock : `${at.getDate()} ${MONTHS[at.getMonth()]} ${clock}`;
}

/**
 * What the live pill says it is holding. The number is the rows this page has
 * loaded, not how many the pipeline has emitted, so it names itself as such
 * and says where the loaded list ends; totals are the summary strip's job.
 */
export function loadedCountText(events: Array<Pick<PipelineEvent, 'ts'>>, now: Date): string {
  if (events.length === 0) return 'Showing 0 events';
  // The feed is newest first, so the oldest loaded event is the last one.
  return `Showing ${events.length} since ${loadedSinceText(events[events.length - 1].ts, now)}`;
}

export interface DayGroup {
  day: string;
  events: PipelineEvent[];
}

/** The feed split into runs of one day, in the order the events came (newest first). */
export function groupByDay(events: PipelineEvent[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const event of events) {
    const day = localDayKey(event.ts);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.events.push(event);
    else groups.push({ day, events: [event] });
  }
  return groups;
}

/** Whether a row has more to say than its one line: a reason, a log, or a
 *  field its producer attached that the open row names. */
export function hasDetail(event: Pick<PipelineEvent, 'reason' | 'log_tail' | 'kind' | 'detail'>): boolean {
  return Boolean(event.reason) || Boolean(event.log_tail) || eventDetailFields(event).length > 0;
}

// ---- The fields a producer attaches to an event ---------------------------

/**
 * One field of `detail`, as the open row names it. The producer sends a bag of
 * keys; a row shows the few that answer the question its kind raises — which
 * runner ran the gate, which model took the attempt, what command would deploy
 * the release it just staged.
 */
export interface DetailField {
  /** The key as read, so a row can key on it and a test can name it. */
  key: string;
  /** What the field is, in words: `Runner`, `Deploy command`. */
  label: string;
  value: string;
  /** Shown as given when the row shortens it: a full sha behind `b761244b`. */
  title?: string;
  /** Drawn in the mono face: a sha, a path, a command, a release name. */
  mono: boolean;
  /** Worth handing over whole, because nobody should retype it. */
  copy: boolean;
}

/** What each key is called, in the words the rest of the screen uses. */
const DETAIL_LABELS: Record<string, string> = {
  stage: 'Stage',
  slot: 'Slot',
  runner: 'Runner',
  recipe: 'Recipe',
  receipt: 'Receipt',
  model: 'Model',
  attempt: 'Attempt',
  attempts: 'Attempts',
  shift: 'Shift',
  head_sha: 'Head',
  release: 'Release',
  deploy_command: 'Deploy command',
  log_path: 'Log path',
};

/** The keys drawn in the mono face: machine words, not prose. */
const DETAIL_MONO = new Set(['recipe', 'receipt', 'head_sha', 'release', 'deploy_command', 'log_path', 'shift']);

/** The keys worth a copy button: a line a human is meant to run. */
const DETAIL_COPY = new Set(['deploy_command']);

/**
 * Which keys each family of kinds shows, in the order it shows them. The first
 * prefix a kind starts with wins, so `gate.log` and `gate.finished` answer the
 * same question; a kind no prefix covers shows nothing rather than every key
 * the producer happened to attach.
 */
const DETAIL_KEYS: readonly { prefix: string; keys: readonly string[] }[] = [
  { prefix: 'gate.', keys: ['runner', 'recipe', 'receipt'] },
  { prefix: 'todo.', keys: ['model', 'attempt', 'attempts', 'shift'] },
  { prefix: 'worker.', keys: ['stage', 'slot', 'runner', 'head_sha'] },
  { prefix: 'release.', keys: ['release', 'head_sha', 'deploy_command'] },
  { prefix: 'deploy.', keys: ['release', 'log_path'] },
  { prefix: 'queue.', keys: ['head_sha', 'attempts'] },
  { prefix: 'review.', keys: ['model', 'runner'] },
];

/** At most this many fields per row: the open row answers a question, it is
 *  not the event in full — `Copy JSON` at its foot is. */
export const MAX_DETAIL_FIELDS = 4;

/** A key's value as one line, or null when the producer sent nothing usable. */
function detailValue(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() === '' ? null : value.trim();
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return null;
}

/**
 * The fields the open row shows for this event, in their kind's order, holding
 * only the keys its producer actually sent. Empty for a kind with no fields of
 * its own, and for one whose producer attached no `detail`.
 */
export function eventDetailFields(event: Pick<PipelineEvent, 'kind' | 'detail'>): DetailField[] {
  const detail = event.detail;
  if (!detail) return [];
  const keys = DETAIL_KEYS.find((entry) => event.kind.startsWith(entry.prefix))?.keys ?? [];
  const fields: DetailField[] = [];
  for (const key of keys) {
    const value = detailValue(detail[key]);
    if (value === null) continue;
    // A sha is shown as its first eight characters, like every other sha on the
    // screen, with the whole of it in the title.
    const short = key === 'head_sha' && /^[0-9a-f]{12,}$/i.test(value);
    fields.push({
      key,
      label: DETAIL_LABELS[key] ?? key,
      value: short ? value.slice(0, 8) : value,
      ...(short ? { title: value } : {}),
      mono: DETAIL_MONO.has(key),
      copy: DETAIL_COPY.has(key),
    });
    if (fields.length === MAX_DETAIL_FIELDS) break;
  }
  return fields;
}

/** The five numbers the 24-hour strip shows, wherever they were counted. */
export interface ActivityTotals {
  todosFinished: number;
  blocked: number;
  prsMerged: number;
  deploys: number;
  spentUsd: number;
}

export interface WallCounters extends ActivityTotals {
  /** Events inside the window, so the wall can say what the counters cover. */
  events: number;
}

export interface ActivityCounters extends ActivityTotals {
  /** Events the numbers were counted from, or null when the server totalled
   *  the whole window itself and the count of loaded rows means nothing. */
  events: number | null;
}

/**
 * The strip's numbers: the server's window totals when it sends them, else
 * what the events this page holds add up to — with their count, so the strip
 * can say the numbers only cover what is loaded here.
 */
export function activityCounters(
  totals: ActivityTotals | null,
  events: PipelineEvent[],
  now: Date
): ActivityCounters {
  return totals ? { ...totals, events: null } : wallCounters(events, now);
}

/**
 * Last-24-hour counters, computed from the fetched events only: the wall says
 * how many events it covers rather than implying a server-side total.
 */
export function wallCounters(events: PipelineEvent[], now: Date): WallCounters {
  const since = now.getTime() - DAY_MS;
  const counters: WallCounters = {
    todosFinished: 0,
    blocked: 0,
    prsMerged: 0,
    deploys: 0,
    spentUsd: 0,
    events: 0,
  };
  for (const event of events) {
    const at = Date.parse(event.ts);
    if (Number.isNaN(at) || at < since) continue;
    counters.events += 1;
    if (typeof event.cost_usd === 'number') counters.spentUsd += event.cost_usd;
    if (event.kind === 'todo.attempt_finished') {
      if (event.outcome === 'done') counters.todosFinished += 1;
      if (event.outcome === 'blocked') counters.blocked += 1;
    }
    if (event.kind === 'pr.merged') counters.prsMerged += 1;
    if (event.kind === 'deploy.status' && event.outcome === 'success') counters.deploys += 1;
  }
  counters.spentUsd = Math.round(counters.spentUsd * 100) / 100;
  return counters;
}

/**
 * What happened, in words an operator reads at a glance. The wire kind
 * (`gate.log`, `todo.attempt_finished`) is for machines; a kind this table
 * does not know is shown as it came, never hidden.
 */
export function eventLabel(event: Pick<PipelineEvent, 'kind' | 'outcome'>): string {
  const outcome = (event.outcome ?? '').toLowerCase();
  const failed = outcome === 'failure' || outcome === 'error' || outcome === 'failed';
  switch (event.kind) {
    case 'gate.started':
      return 'Gate started';
    case 'gate.log':
    case 'gate.finished':
      if (outcome === 'success') return 'Gate passed';
      if (outcome === 'timed_out') return 'Gate timed out';
      if (outcome === 'inputs_changed') return 'Gate re-running';
      return failed ? 'Gate failed' : 'Gate finished';
    case 'review.started':
      return 'Review started';
    case 'review.finished':
    case 'pr.review':
      if (outcome === 'approve' || outcome === 'approved') return 'Review: approved';
      if (outcome === 'request_changes' || outcome === 'hold') return 'Review: changes requested';
      return failed ? 'Review failed' : 'Review finished';
    case 'pr.opened':
      return 'Pull request opened';
    case 'pr.approved':
      return 'Pull request approved';
    case 'pr.merged':
      return 'Pull request merged';
    case 'queue.enqueued':
      return 'Queued to merge';
    case 'queue.building':
      return 'Merge queue gating';
    case 'queue.landed':
      return 'Merged by the queue';
    case 'queue.failed':
      return 'Merge queue failed';
    case 'queue.dequeued':
      return 'Left the merge queue';
    case 'queue.refused':
      return 'Merge queue refused';
    case 'todo.filed':
      return 'Todo filed';
    case 'todo.claimed':
      return 'Todo claimed';
    case 'todo.action':
      return 'Todo changed';
    case 'todo.merged':
      return 'Todo merged';
    case 'todo.waiting_on_unmerged':
      return 'Todo waiting';
    case 'todo.attempt_finished':
      if (outcome === 'done') return 'Todo finished';
      if (outcome === 'blocked') return 'Todo blocked';
      if (outcome === 'retry') return 'Todo retrying';
      if (outcome === 'ratelimit') return 'Rate limited';
      return 'Attempt finished';
    case 'worker.stage':
      return 'Worker';
    case 'worker.error':
      return 'Worker error';
    case 'shift.pr_opened':
      return 'Shift pull request opened';
    case 'shift.exhausted':
      return 'Shift budget spent';
    case 'release.staged':
      return 'Release staged';
    case 'release.stage_failed':
      return 'Staging failed';
    case 'deploy.created':
      return 'Deploy started';
    case 'deploy.status':
      if (outcome === 'success') return 'Deployed';
      return failed ? 'Deploy failed' : 'Deploying';
    case 'pin.bump_opened':
      return 'Pin bump opened';
    case 'pin.bump_failed':
      return 'Pin bump failed';
    default:
      return event.kind;
  }
}

/** One-click views of the feed. `kind` is a server-side prefix filter. */
export interface ActivityChip {
  id: string;
  label: string;
  kind?: string;
  needsHuman?: boolean;
}

export const ACTIVITY_CHIPS: readonly ActivityChip[] = [
  { id: 'all', label: 'All' },
  // Past tense on purpose: these are events that were flagged, not the list of
  // what waits on someone now — that is Needs you, linked beside the chips.
  { id: 'human', label: 'Needed a human', needsHuman: true },
  { id: 'todos', label: 'Todos', kind: 'todo.' },
  // The slots themselves: the stage beats of an attempt (`worker.stage`), what
  // went wrong in one (`worker.error`) and what a shift did with it
  // (`shift.*`). Together they are the loudest thing in the feed, which is why
  // All leaves the step beats out and this chip is where they are read.
  { id: 'workers', label: 'Workers', kind: 'worker.,shift.' },
  { id: 'gates', label: 'Gates', kind: 'gate.' },
  // A verdict is `pr.review`; the reviewer's start/finish beats are `review.*`.
  { id: 'reviews', label: 'Reviews', kind: 'review.,pr.review' },
  { id: 'pulls', label: 'Pull requests', kind: 'pr.' },
  // A merge is reported by whichever road it took: `queue.*` through the merge
  // queue, `pr.merged` from a direct merge. Naming only the queue made the chip
  // go quiet whenever the queue was not in the way.
  { id: 'merges', label: 'Merges', kind: 'queue.,pr.merged' },
  { id: 'releases', label: 'Releases', kind: 'release.' },
  { id: 'deploys', label: 'Deploys', kind: 'deploy.' },
];

/** The chip the URL's filters amount to, or null for a hand-made filter. */
export function activeChip(filters: Pick<ActivityFilters, 'kind' | 'needs_human'>): string | null {
  if (filters.needs_human) return filters.kind === '' ? 'human' : null;
  if (filters.kind === '') return 'all';
  return ACTIVITY_CHIPS.find((chip) => chip.kind === filters.kind)?.id ?? null;
}

/** The URL params a chip sets, keeping the family and the other filters. */
export function applyChip(params: URLSearchParams, chip: ActivityChip): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('kind');
  next.delete('needs_human');
  if (chip.kind) next.set('kind', chip.kind);
  if (chip.needsHuman) next.set('needs_human', '1');
  return next;
}

/** Filters that live behind "More filters": open it when one of them is set. */
export const MORE_FILTER_KEYS = ['repo', 'source', 'kind', 'todo_id', 'pr'] as const;

/**
 * Whether one of the folded filters is set, which is reason to unfold them on
 * arrival. A repository's Activity tab pins the repo and hides the Repo field,
 * so that pin is not one of the filters a reader could see or clear:
 * `pinnedRepo` leaves it out and the panel stays folded there.
 */
export function hasMoreFilters(filters: ActivityFilters, pinnedRepo = false): boolean {
  const chipKind = ACTIVITY_CHIPS.some((chip) => chip.kind === filters.kind);
  return MORE_FILTER_KEYS.some(
    (key) =>
      filters[key] !== '' && !(key === 'kind' && chipKind) && !(key === 'repo' && pinnedRepo)
  );
}

/** The event's main subject as one link: its pull request, else its todo, else its repo. */
export function primaryLink(event: PipelineEvent, host: string): EventLink | null {
  if (event.repo && event.pr) {
    return { label: `${event.repo}#${event.pr}`, to: pullHref(host, event.repo, event.pr) };
  }
  if (event.todo_id && event.family) {
    return { label: `todo ${event.todo_id}`, to: todoHref(event.todo_id, event.family) };
  }
  if (event.repo) return { label: event.repo, to: repoHref(host, event.repo) };
  return null;
}

/**
 * The summary with its subject turned into the link. A gate summary already
 * says `jeryu/jeryu-deploy#57`; printing that again beside it was noise. When
 * the summary does not name the subject, the link follows it.
 */
export interface SummaryParts {
  before: string;
  link: EventLink | null;
  after: string;
}

export function summaryParts(event: PipelineEvent, host: string): SummaryParts {
  const link = primaryLink(event, host);
  if (!link) return { before: event.summary, link: null, after: '' };
  const at = event.summary.indexOf(link.label);
  if (at < 0) return { before: `${event.summary} `, link, after: '' };
  return {
    before: event.summary.slice(0, at),
    link,
    after: event.summary.slice(at + link.label.length),
  };
}


/**
 * How long a `in …` clause says something took, in seconds, or null when the
 * clause is not one: `in production` reads as a place, not a duration.
 */
function clauseSeconds(hours?: string, minutes?: string, seconds?: string): number | null {
  if (hours === undefined && minutes === undefined && seconds === undefined) return null;
  return Number(hours ?? 0) * 3600 + Number(minutes ?? 0) * 60 + Number(seconds ?? 0);
}

/** A duration at the end of a summary: `in 91s`, `in 1m 31s`, `in 2h 5m`. */
const TRAILING_DURATION = /\s+in\s+(?:(\d+)\s*h\s*)?(?:(\d+)\s*m\s*)?(?:(\d+(?:\.\d+)?)\s*s)?\s*$/;

/**
 * The summary a row shows: the one the producer wrote, minus a closing
 * duration the row's own duration column already says. A gate reports
 * `success in 91s` and the column reads `1m 31s` beside it — the same number
 * twice, spelled two ways. Only a trailing `in …` that agrees with `seconds`
 * goes, so a summary whose number means something else keeps every word.
 */
export function summaryText(event: Pick<PipelineEvent, 'summary' | 'seconds'>): string {
  const seconds = event.seconds;
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return event.summary;
  const said = TRAILING_DURATION.exec(event.summary);
  if (!said) return event.summary;
  const clause = clauseSeconds(said[1], said[2], said[3]);
  if (clause === null || Math.abs(clause - seconds) > 0.5) return event.summary;
  return event.summary.slice(0, said.index);
}

function sameSubject(a: PipelineEvent, b: PipelineEvent): boolean {
  if (a.kind.split('.')[0] !== b.kind.split('.')[0]) return false;
  if (a.repo !== b.repo) return false;
  if (a.pr !== null && a.pr !== b.pr) return false;
  if (a.todo_id !== null && a.todo_id !== b.todo_id) return false;
  return true;
}

/** The two reports of one finished gate (heartbeat, then the runner's log). */
const GATE_KINDS = new Set(['gate.log', 'gate.finished']);

/** Kinds that say the PR's work is over: nothing on it waits on a person. */
const LANDED_KINDS = new Set(['pr.merged', 'queue.landed', 'todo.merged']);

function outcomeOf(event: Pick<PipelineEvent, 'outcome'>): string {
  return (event.outcome ?? '').toLowerCase();
}

function isGateFailure(event: PipelineEvent): boolean {
  return GATE_KINDS.has(event.kind) && BAD.has(outcomeOf(event));
}

/**
 * Whether `later` clears a gate failure: the pull request it failed on was
 * merged, or a gate on it has since passed. A gate failure is about the PR,
 * not about the gate as a subject of its own, so a merge counts even though
 * its kind family (`pr.`, `queue.`) differs from `gate.`.
 */
function clearsGateFailure(failure: PipelineEvent, later: PipelineEvent): boolean {
  if (!isGateFailure(failure) || failure.repo === null || failure.pr === null) return false;
  if (later.repo !== failure.repo || later.pr !== failure.pr) return false;
  if (LANDED_KINDS.has(later.kind)) return !BAD.has(outcomeOf(later));
  return GATE_KINDS.has(later.kind) && outcomeOf(later) === 'success';
}

/**
 * Seqs of rows whose cause has since cleared: a row that once needed a person,
 * or a gate that failed, followed by a later event that says the cause is
 * gone. For most subjects that is a clean finish on the same subject (same
 * kind family, repo, PR, todo); for a gate failure it is also a merge or a
 * green gate on the same pull request. Needs you already drops these; history
 * must not keep calling for attention, or the pill stops meaning anything.
 */
export function resolvedSeqs(events: PipelineEvent[]): Set<number> {
  const resolved = new Set<number>();
  for (const event of events) {
    if (!event.needs_human && !isGateFailure(event)) continue;
    const cleared = events.some(
      (later) =>
        later.seq > event.seq &&
        !later.needs_human &&
        ((GOOD.has(outcomeOf(later)) && sameSubject(event, later)) || clearsGateFailure(event, later))
    );
    if (cleared) resolved.add(event.seq);
  }
  return resolved;
}

// ---- Runs of worker stage beats -------------------------------------------

/** The kind a working slot reports its progress through an attempt with. */
export const WORKER_STAGE_KIND = 'worker.stage';

/** The stages a slot walks on one attempt, in the order it walks them. */
export const WORKER_STAGES = ['prepare', 'agent', 'gate', 'land'] as const;

const STAGE_WORD = /^[a-z][a-z0-9_]*$/;

/** `w1 jeryu: agent -> gate on 20260919-0800-aaa`, the words a beat is written
 *  in when its producer attached no `detail`: the slot, the stage it left, and
 *  the stage it reached. */
const STAGE_SUMMARY = /^(w\d+)\b[^:]*:\s*(?:([a-z][a-z0-9_]*)\s*->\s*)?([a-z][a-z0-9_]*)/;

function isStage(stage: string): boolean {
  return (WORKER_STAGES as readonly string[]).includes(stage);
}

function detailText(event: Pick<PipelineEvent, 'detail'>, key: string): string | null {
  const value = event.detail?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * Which slot a beat came from: `w1`. The producer's own `detail.slot` first,
 * then the slot of its actor (`operator@node-0/w1`), then the summary. Null when
 * nothing names one — two slots then cannot be told apart, so their beats are
 * never folded into one row.
 */
export function stageSlot(event: PipelineEvent): string | null {
  const stated = detailText(event, 'slot');
  if (stated) return stated;
  const actor = (event.actor ?? '').split('/').pop() ?? '';
  if (/^w\d+$/.test(actor)) return actor;
  return STAGE_SUMMARY.exec(event.summary)?.[1] ?? null;
}

/** The stage a beat left, when it says so: the first of them opens the path
 *  a run walked, which otherwise would start where its second beat starts. */
export function stageFrom(event: PipelineEvent): string | null {
  const stated = detailText(event, 'from');
  if (stated !== null && STAGE_WORD.test(stated)) return stated;
  return STAGE_SUMMARY.exec(event.summary)?.[2] ?? null;
}

/** The stage a beat reached, from `detail.stage`, its outcome, or its summary. */
export function stageName(event: PipelineEvent): string | null {
  const stated = detailText(event, 'stage');
  if (stated !== null && STAGE_WORD.test(stated)) return stated;
  const outcome = (event.outcome ?? '').toLowerCase();
  if (isStage(outcome)) return outcome;
  return STAGE_SUMMARY.exec(event.summary)?.[3] ?? null;
}

/** Consecutive beats of one slot on one todo, as the one row they are drawn as. */
export interface StageRun {
  /** The newest beat: the row's time, its tone, and its key. */
  latest: PipelineEvent;
  /** Every beat folded here, newest first, as the feed held them. */
  events: PipelineEvent[];
  slot: string;
  todoId: string | null;
  /** The stages the run walked, oldest first; the last is where it is now. */
  steps: string[];
}

/**
 * What a list draws: one event, a run of stage beats folded into one row, or a
 * burst of identical reports folded into one row. At most one of `run` and
 * `burst` is set — a row stands for one kind of fold.
 */
export interface ActivityRow {
  /** The event the row stands for: a lone event, or a fold's newest event. */
  event: PipelineEvent;
  /** Set when this row stands for several beats, which the row can expand. */
  run: StageRun | null;
  /** Set when this row stands for a burst of identical reports. */
  burst: EventBurst | null;
}

/** Every event a row stands for, newest first: one, or a whole fold. */
export function rowEvents(row: ActivityRow): PipelineEvent[] {
  if (row.run) return row.run.events;
  if (row.burst) return row.burst.events;
  return [row.event];
}

function stageRun(slot: string, todoId: string | null, events: PipelineEvent[]): StageRun {
  const steps: string[] = [];
  const oldest = events[events.length - 1];
  const opening = stageFrom(oldest);
  if (opening !== null) steps.push(opening);
  for (const event of [...events].reverse()) {
    const stage = stageName(event);
    if (stage !== null && steps[steps.length - 1] !== stage) steps.push(stage);
  }
  return { latest: events[0], events, slot, todoId, steps };
}

/**
 * The feed as rows, with runs of stage beats folded. A slot reports several
 * beats per attempt (`prepare -> agent`, `agent -> gate`, …) and they buried
 * the outcome rows between them; consecutive beats of one slot on one todo
 * become one row that says the whole path and opens to the beats themselves.
 * A lone beat stays a plain row: there is nothing to fold and nothing hidden.
 */
export function foldStageRuns(events: PipelineEvent[]): ActivityRow[] {
  const rows: ActivityRow[] = [];
  let open: { slot: string; todoId: string | null; events: PipelineEvent[] } | null = null;
  const close = (): void => {
    if (open === null) return;
    const beats = open.events;
    rows.push(
      beats.length === 1
        ? { event: beats[0], run: null, burst: null }
        : { event: beats[0], run: stageRun(open.slot, open.todoId, beats), burst: null }
    );
    open = null;
  };
  for (const event of events) {
    const slot = event.kind === WORKER_STAGE_KIND ? stageSlot(event) : null;
    if (slot === null) {
      close();
      rows.push({ event, run: null, burst: null });
      continue;
    }
    if (open !== null && open.slot === slot && open.todoId === event.todo_id) {
      open.events.push(event);
      continue;
    }
    close();
    open = { slot, todoId: event.todo_id, events: [event] };
  }
  close();
  return rows;
}

/** One step of the ladder a folded run draws. */
export interface StageStep {
  stage: string;
  /** The run reported this stage. */
  walked: boolean;
  /** Where the slot is now: the newest beat's stage. */
  current: boolean;
}

/**
 * The whole ladder, so the row reads the same whichever stage a run is on:
 * the four stages of an attempt, plus any stage this build does not know in
 * the order the run walked it — a stage is never dropped for being unknown.
 */
export function stageSteps(run: StageRun): StageStep[] {
  const walked = new Set(run.steps);
  const extra = run.steps.filter((stage) => !isStage(stage));
  const ladder = [...WORKER_STAGES, ...new Set(extra)];
  const current = run.steps[run.steps.length - 1] ?? null;
  return ladder.map((stage) => ({ stage, walked: walked.has(stage), current: stage === current }));
}

/** The run in one line, for the row's `title`: how many beats, and the path. */
export function stageRunTitle(run: StageRun): string {
  const beats = `${run.events.length} stage event${run.events.length === 1 ? '' : 's'}`;
  return run.steps.length === 0 ? beats : `${beats}: ${run.steps.join(' → ')}`;
}

// ---- Bursts of identical reports ------------------------------------------

/**
 * How far apart the ends of one burst may lie. A reviewer walking a family of
 * repositories files the same verdict on each of them within a few minutes;
 * half an hour later it is a second pass, and reading as one row would hide
 * that the same thing happened twice.
 */
export const BURST_MS = 30 * 60 * 1000;

/**
 * Consecutive reports that say the same thing about different subjects, as the
 * one row they are drawn as: a reviewer requesting changes on twelve pull
 * requests filled a screen with twelve rows that differed only in a number.
 */
export interface EventBurst {
  /** The newest report: the row's time, its tone, and its key. */
  latest: PipelineEvent;
  /** Every report folded here, newest first, as the feed held them. */
  events: PipelineEvent[];
  /** What they share, and what the fold is keyed by. */
  kind: string;
  actor: string | null;
  outcome: string | null;
  reason: string | null;
}

/** The repository names a burst touched, each without its owner. */
function burstSubjects(events: PipelineEvent[]): string[] {
  const subjects: string[] = [];
  for (const event of events) {
    const name = event.repo?.split('/').pop() ?? null;
    if (name !== null && name !== '') subjects.push(name);
  }
  return subjects;
}

/** The longest start every name shares, with a trailing separator dropped. */
function sharedStem(names: string[]): string {
  if (names.length === 0) return '';
  let stem = names[0];
  for (const name of names.slice(1)) {
    let at = 0;
    while (at < stem.length && at < name.length && stem[at] === name[at]) at += 1;
    stem = stem.slice(0, at);
  }
  return stem.replace(/[-_.]+$/, '');
}

/**
 * What a burst happened to, counted: `12 jain pull requests` when the
 * repositories share a stem, `12 pull requests` when they share nothing,
 * `12 todos` when the subject is a todo rather than a pull request, and
 * `12 events` otherwise. A stem of one character is no name, so it is not
 * printed as one.
 */
export function burstSubjectText(events: PipelineEvent[]): string {
  const count = events.length;
  if (events.every((event) => event.pr !== null)) {
    const stem = sharedStem(burstSubjects(events));
    const subject = `pull request${count === 1 ? '' : 's'}`;
    return stem.length > 1 ? `${count} ${stem} ${subject}` : `${count} ${subject}`;
  }
  if (events.every((event) => event.todo_id !== null)) return `${count} todo${count === 1 ? '' : 's'}`;
  return `${count} event${count === 1 ? '' : 's'}`;
}

/**
 * What the actor did, as a verb the count can follow: `requested changes on`,
 * `merged`. A kind this table does not know falls back to its own label, so a
 * burst of a kind this build has never seen still reads as a sentence.
 */
function burstVerb(event: Pick<PipelineEvent, 'kind' | 'outcome'>): string {
  const outcome = (event.outcome ?? '').toLowerCase();
  switch (event.kind) {
    case 'review.finished':
    case 'pr.review':
      if (outcome === 'approve' || outcome === 'approved') return 'approved';
      if (outcome === 'request_changes' || outcome === 'hold') return 'requested changes on';
      return 'reviewed';
    case 'pr.opened':
      return 'opened';
    case 'pr.approved':
      return 'approved';
    case 'pr.merged':
      return 'merged';
    case 'queue.enqueued':
      return 'queued';
    case 'queue.landed':
      return 'landed';
    case 'gate.log':
    case 'gate.finished':
      return outcome === 'success' ? 'gated' : 'failed the gate on';
    case 'todo.filed':
      return 'filed';
    case 'todo.claimed':
      return 'claimed';
    default:
      return `${eventLabel(event).toLowerCase()} on`;
  }
}

/** Who a burst is attributed to: the last part of the actor, else its source. */
export function burstActor(event: Pick<PipelineEvent, 'actor' | 'source'>): string {
  const actor = (event.actor ?? '').split('/').pop()?.trim() ?? '';
  return actor === '' ? event.source : actor;
}

/** The burst in one line: `review-bot requested changes on 12 jain pull requests`. */
export function burstTitle(burst: EventBurst): string {
  return `${burstActor(burst.latest)} ${burstVerb(burst.latest)} ${burstSubjectText(burst.events)}`;
}

/** Whether two reports say the same thing: the fold's key, read off a pair. */
function sameReport(a: PipelineEvent, b: PipelineEvent): boolean {
  return a.kind === b.kind && a.actor === b.actor && a.outcome === b.outcome && a.reason === b.reason;
}

/** How many identical reports in a row are a burst rather than a list. */
export const MIN_BURST_EVENTS = 3;

/**
 * The rows with bursts of identical reports folded. Rows already folded as a
 * stage run are left alone: a row stands for one kind of fold.
 *
 * A row joins the open burst when it says the same thing (same kind, actor,
 * outcome and reason) and happened within `BURST_MS` of the burst's newest
 * report — the span of the whole burst is bounded, not just the gap to its
 * neighbour, so a steady trickle never grows into one row covering a day. A
 * burst also stays within one local day, because the row sits under a day
 * header and is counted towards that day's totals.
 *
 * Anything else between two reports closes the burst, the same way it closes
 * a stage run: the fold only ever hides rows that were adjacent.
 */
export function foldBursts(rows: ActivityRow[]): ActivityRow[] {
  const folded: ActivityRow[] = [];
  let open: ActivityRow[] = [];
  const close = (): void => {
    if (open.length === 0) return;
    if (open.length < MIN_BURST_EVENTS) folded.push(...open);
    else {
      const events = open.map((row) => row.event);
      const latest = events[0];
      folded.push({
        event: latest,
        run: null,
        burst: {
          latest,
          events,
          kind: latest.kind,
          actor: latest.actor,
          outcome: latest.outcome,
          reason: latest.reason,
        },
      });
    }
    open = [];
  };
  for (const row of rows) {
    if (row.run !== null || row.burst !== null) {
      close();
      folded.push(row);
      continue;
    }
    const first = open[0];
    if (
      first !== undefined &&
      sameReport(first.event, row.event) &&
      Math.abs(Date.parse(first.event.ts) - Date.parse(row.event.ts)) <= BURST_MS &&
      localDayKey(first.event.ts) === localDayKey(row.event.ts)
    ) {
      open.push(row);
      continue;
    }
    close();
    open = [row];
  }
  close();
  return folded;
}

/** The rows' seqs, in order: what a keyboard walks and an arrival is keyed by. */
export function rowSeqs(rows: ActivityRow[]): number[] {
  return rows.map((row) => row.event.seq);
}

export interface RowDayGroup {
  day: string;
  rows: ActivityRow[];
}

/** The rows split into runs of one day, the same way `groupByDay` splits events. */
export function groupRowsByDay(rows: ActivityRow[]): RowDayGroup[] {
  const groups: RowDayGroup[] = [];
  for (const row of rows) {
    const day = localDayKey(row.event.ts);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.rows.push(row);
    else groups.push({ day, rows: [row] });
  }
  return groups;
}

/** What a day header says about the rows under it, counted from those rows. */
export interface DayTotals {
  /** Events in the group, counting every beat a folded run holds. */
  events: number;
  /** Events that failed or want a human: what a scroll should not miss. */
  failed: number;
  /** Dollars the day's loaded events report, to the cent. */
  spentUsd: number;
}

/**
 * A day's loaded volume, from the rows the group draws. A folded run counts
 * its beats, not the one line it is drawn as, so the number agrees with the
 * feed's own event count rather than with how the rows happen to be grouped.
 */
export function dayTotals(rows: ActivityRow[]): DayTotals {
  const totals: DayTotals = { events: 0, failed: 0, spentUsd: 0 };
  for (const row of rows) {
    for (const event of rowEvents(row)) {
      totals.events += 1;
      const tone = eventTone(event);
      if (tone === 'failed' || tone === 'human') totals.failed += 1;
      if (typeof event.cost_usd === 'number') totals.spentUsd += event.cost_usd;
    }
  }
  totals.spentUsd = Math.round(totals.spentUsd * 100) / 100;
  return totals;
}

/**
 * The day's numbers in one line, for the header the rows sit under: the count
 * always, the other two only when there is something to say — a day with no
 * failures and no spend should not read as a row of zeroes.
 */
export function dayTotalsText(totals: DayTotals): string {
  const parts = [`${totals.events} event${totals.events === 1 ? '' : 's'}`];
  if (totals.failed > 0) parts.push(`${totals.failed} failed`);
  if (totals.spentUsd > 0) parts.push(`$${totals.spentUsd.toFixed(2)}`);
  return parts.join(' · ');
}

/** What the live tail is doing, as the status pill above the list says it. */
export type TailPhase = 'live' | 'paused' | 'reconnecting';

export const TAIL_PHASE_LABEL: Record<TailPhase, string> = {
  live: 'Live',
  paused: 'Paused',
  reconnecting: 'Reconnecting',
};

/**
 * A failing tail is the loudest thing to say, so it wins over a hidden tab or
 * a reader's own Pause: they come back to "Reconnecting" rather than to a calm
 * "Paused".
 */
export function tailPhase(state: { failing: boolean; hidden: boolean }): TailPhase {
  if (state.failing) return 'reconnecting';
  if (state.hidden) return 'paused';
  return 'live';
}

/**
 * How long ago the feed last heard back: `updated 4s ago`, in seconds while
 * that is the interesting scale, then minutes and hours. Empty until a page
 * has arrived, so the pill says nothing it does not know.
 */
export function updatedAgoText(updatedAt: number | null, now: number): string {
  if (updatedAt === null || updatedAt <= 0) return '';
  const seconds = Math.max(0, Math.floor((now - updatedAt) / 1_000));
  if (seconds < 60) return `updated ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `updated ${minutes}m ago`;
  return `updated ${Math.floor(minutes / 60)}h ago`;
}
